/**
 * Shared helpers for customer photo/file attachments on quote forms.
 *
 * Two things used to break real customer uploads:
 *  1. Browsers (especially iOS Safari with HEIC photos and some Android
 *     galleries) hand us a File with an EMPTY `file.type`. We used to send
 *     "application/octet-stream", which the storage bucket rejects.
 *  2. Files whose name had no extension (or an unusual one) failed the storage
 *     rules, which require a known image/pdf extension.
 *
 * These helpers normalize both so the upload actually goes through.
 */

/** Extensions the storage buckets + policies accept. */
const EXT_TO_MIME: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jfif: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heic",
  pdf: "application/pdf",
};

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/pjpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heic",
  "image/heic-sequence": "heic",
  "image/heif-sequence": "heic",
  "application/pdf": "pdf",
};

/** Human-friendly list used in the file input `accept` attribute. */
export const ATTACHMENT_ACCEPT =
  ".jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.pdf,image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf";

export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024; // 20MB (bucket limit)

function extOf(name: string): string {
  const m = /\.([a-zA-Z0-9]+)$/.exec(name.trim());
  return m ? m[1].toLowerCase() : "";
}

/**
 * Best-effort content type for a file. Falls back to the file extension when
 * the browser gives us nothing (common with iPhone HEIC photos).
 * Returns null when we can't map it to something storage accepts.
 */
export function attachmentContentType(file: File): string | null {
  const declared = (file.type || "").toLowerCase().split(";")[0].trim();
  if (declared && MIME_TO_EXT[declared]) return MIME_TO_EXT[declared] === "jpg" ? "image/jpeg" : declared === "image/heif" ? "image/heic" : declared;
  const ext = extOf(file.name);
  if (ext && EXT_TO_MIME[ext]) return EXT_TO_MIME[ext];
  return null;
}

/**
 * Sanitized filename that is guaranteed to end in an accepted extension so the
 * storage policy allows the upload.
 */
export function safeAttachmentName(file: File, maxLen = 80): string {
  const contentType = attachmentContentType(file) ?? "image/jpeg";
  const wantedExt = MIME_TO_EXT[contentType] ?? "jpg";
  let base = (file.name || "photo").replace(/[^a-zA-Z0-9._-]/g, "_");
  const currentExt = extOf(base);
  if (currentExt) base = base.slice(0, -(currentExt.length + 1));
  if (!base) base = "photo";
  const finalExt = currentExt && EXT_TO_MIME[currentExt] ? currentExt : wantedExt;
  base = base.slice(0, Math.max(1, maxLen - finalExt.length - 1));
  return `${base}.${finalExt}`;
}

/**
 * Validates a list of files before upload. Returns an error message to show
 * the customer, or null when everything is good.
 */
export function validateAttachments(files: File[]): string | null {
  for (const file of files) {
    if (!attachmentContentType(file)) {
      return `"${file.name}" isn't a supported file type. Please upload JPG, PNG, WEBP, GIF, HEIC or PDF.`;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      return `"${file.name}" is too large (max 20MB per file). Please send a smaller photo.`;
    }
  }
  return null;
}
